/**
 * Round 80 — A-C-12 Governance & Explainability
 *
 * Pure builders that turn already-loaded row data into a standardized
 * `OptiBayExplanation`. No fetches, no side effects (except a dev-only
 * language guard). One builder per metric/surface scope.
 */
import type { OptiBayExplanation } from './types';
import { assertSafeLanguage } from './terminology';
import {
  BEFORE_AFTER_WINDOW_N,
  DIAGNOSTIC_DELTA_MIN_PER_SIDE,
  DIAGNOSTIC_MIN_SAMPLE_FOR_DISPLAY,
  DIAGNOSTIC_MIN_SAMPLE_PER_TECH,
  FIT_CONFIDENCE_RULES,
  FIT_SIGNAL_THRESHOLDS,
  NO_CHANGE_DELTA,
  TRAINING_VISIBILITY_NOTE,
} from './thresholds';
import type { ScopeTalentBreakdownRow, ScopeTalentSummary } from '@/hooks/useScopeTalentAnalytics';
import type {
  ScopeTrainingArtifactRow,
  ScopeTechnicianTrainingRow,
  ScopeTrainingSummary,
} from '@/hooks/useScopeTrainingAnalytics';
import type { ScopeArtifactIntelligenceRow } from '@/hooks/useScopeTrainingEffectiveness';
import type { ScopeFitSuggestion, FitSignalKey } from '@/hooks/useScopeFitSuggestions';

function pct(v: number | null | undefined, digits = 0): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(digits)}%`;
}

function confidenceFromJobs(jobs: number): 'low' | 'medium' | 'high' {
  if (jobs >= 50) return 'high';
  if (jobs >= 20) return 'medium';
  return 'low';
}

function finalize(exp: OptiBayExplanation, surface: string): OptiBayExplanation {
  assertSafeLanguage(exp.summary, surface);
  return exp;
}

// ─────────────────────────── R74 — Talent analytics ───────────────────────────

export function explainEfficiency(row: ScopeTalentBreakdownRow): OptiBayExplanation {
  const conf = confidenceFromJobs(row.jobs_completed);
  return finalize(
    {
      summary: `Efficiency is the average ratio of predicted minutes to actual minutes across this technician's completed jobs.`,
      signals: ['Average predicted ÷ actual minutes', 'Completed-ticket data only'],
      data_points: [
        `Based on ${row.jobs_completed.toLocaleString()} completed jobs`,
        `Current value: ${pct(row.avg_efficiency)}`,
      ],
      confidence: conf,
      sample_size: row.jobs_completed,
    },
    'efficiency',
  );
}

export function explainComebackRate(row: ScopeTalentBreakdownRow): OptiBayExplanation {
  const conf = confidenceFromJobs(row.jobs_completed);
  return finalize(
    {
      summary: `Comeback rate is the share of this technician's completed jobs flagged as comebacks.`,
      signals: ['Comeback flag on completed tickets'],
      data_points: [
        `Based on ${row.jobs_completed.toLocaleString()} completed jobs`,
        `Current value: ${pct(row.comeback_rate, 1)}`,
      ],
      confidence: conf,
      sample_size: row.jobs_completed,
    },
    'comeback_rate',
  );
}

export function explainDiagnosticAccuracy(row: ScopeTalentBreakdownRow): OptiBayExplanation {
  const sample = row.diagnostic_sample_size ?? 0;
  const conf: 'low' | 'medium' | 'high' = sample >= 10 ? 'high' : sample >= 5 ? 'medium' : 'low';
  return finalize(
    {
      summary: `Diagnostic accuracy averages outcome scores (Confirmed=1, Partial=0.5, Misdiagnosis=0) across recorded diagnoses.`,
      signals: ['Recorded diagnostic outcomes', 'Confirmed / Partial / Misdiagnosis mapping'],
      data_points: [
        sample > 0
          ? `Across ${sample} diagnostic outcome${sample === 1 ? '' : 's'}`
          : 'No diagnostic outcomes recorded yet',
        `Minimum ${DIAGNOSTIC_MIN_SAMPLE_PER_TECH} outcomes required to display per-row value`,
      ],
      confidence: conf,
      sample_size: sample,
    },
    'diagnostic_accuracy',
  );
}

export function explainTalentSummary(
  metric:
    | 'technician_count'
    | 'total_shop_count'
    | 'active_shop_count'
    | 'avg_efficiency'
    | 'avg_comeback_rate'
    | 'avg_diagnostic_accuracy',
  summary: ScopeTalentSummary | null,
): OptiBayExplanation {
  if (!summary) {
    return finalize(
      {
        summary: 'No data available yet for this metric in your scope.',
        signals: [],
        data_points: ['No completed jobs found'],
        confidence: 'low',
      },
      `talent_summary:${metric}`,
    );
  }
  switch (metric) {
    case 'technician_count':
      return finalize(
        {
          summary: 'Count of distinct active technician memberships in your scope.',
          signals: ['Active membership rows'],
          data_points: [`${summary.technician_count.toLocaleString()} technicians`],
          confidence: 'high',
          last_updated_at: summary.computed_at,
        },
        'talent_summary:technician_count',
      );
    case 'total_shop_count':
      return finalize(
        {
          summary: 'Number of shops included in your current scope selection.',
          signals: ['Hierarchy expansion'],
          data_points: [`${summary.total_shop_count} shops in scope`],
          confidence: 'high',
          last_updated_at: summary.computed_at,
        },
        'talent_summary:total_shop_count',
      );
    case 'active_shop_count':
      return finalize(
        {
          summary: 'Shops in scope with at least one completed ticket in the last 90 days.',
          signals: ['Completed tickets in last 90 days'],
          data_points: [
            `${summary.active_shop_count} of ${summary.total_shop_count} shops active`,
          ],
          confidence: 'high',
          last_updated_at: summary.computed_at,
        },
        'talent_summary:active_shop_count',
      );
    case 'avg_efficiency':
      return finalize(
        {
          summary: 'Average predicted ÷ actual minutes across all completed jobs in scope.',
          signals: ['Per-ticket efficiency averaged across scope'],
          data_points: [`Current value: ${pct(summary.avg_efficiency)}`],
          confidence: 'medium',
          last_updated_at: summary.computed_at,
        },
        'talent_summary:avg_efficiency',
      );
    case 'avg_comeback_rate':
      return finalize(
        {
          summary: 'Share of completed jobs flagged as comebacks across scope.',
          signals: ['Comeback flag on completed tickets'],
          data_points: [`Current value: ${pct(summary.avg_comeback_rate, 1)}`],
          confidence: 'medium',
          last_updated_at: summary.computed_at,
        },
        'talent_summary:avg_comeback_rate',
      );
    case 'avg_diagnostic_accuracy': {
      const n = summary.diagnostic_sample_size;
      return finalize(
        {
          summary:
            n >= DIAGNOSTIC_MIN_SAMPLE_FOR_DISPLAY
              ? 'Average diagnostic outcome score across recorded diagnoses in scope.'
              : 'Insufficient diagnostic outcomes recorded yet to display a scope-wide value.',
          signals: ['Confirmed / Partial / Misdiagnosis mapping'],
          data_points: [
            `Across ${n} diagnostic outcome${n === 1 ? '' : 's'}`,
            `Minimum ${DIAGNOSTIC_MIN_SAMPLE_FOR_DISPLAY} required to display`,
          ],
          confidence: n >= 15 ? 'high' : n >= DIAGNOSTIC_MIN_SAMPLE_FOR_DISPLAY ? 'medium' : 'low',
          sample_size: n,
          last_updated_at: summary.computed_at,
        },
        'talent_summary:avg_diagnostic_accuracy',
      );
    }
  }
}

export function explainBand(
  band: 'top' | 'mid' | 'development' | 'comeback_variance' | 'diagnostic_strength',
  count: number,
): OptiBayExplanation {
  const labelMap: Record<typeof band, string> = {
    top: 'top quartile by efficiency',
    mid: 'middle two quartiles by efficiency',
    development: 'lowest quartile by efficiency',
    comeback_variance: 'top quartile by comeback rate',
    diagnostic_strength: 'top quartile by diagnostic accuracy',
  };
  return finalize(
    {
      summary: `Quartile grouping showing the ${labelMap[band]} within your filtered view.`,
      signals: ['Simple quartile sort', 'Computed in source — no hidden weights'],
      data_points: [`${count} technician${count === 1 ? '' : 's'} in this band`],
      confidence: count >= 4 ? 'medium' : 'low',
    },
    `band:${band}`,
  );
}

// ─────────────────────────── R75 — Training visibility ───────────────────────

export function explainTrainingSummary(
  metric: 'total_events' | 'engaged_techs' | 'avg_per_tech' | 'top_format',
  summary: ScopeTrainingSummary | null,
): OptiBayExplanation {
  const last = summary?.computed_at;
  switch (metric) {
    case 'total_events':
      return finalize(
        {
          summary: 'Total training engagement events recorded across your scope.',
          signals: ['Training engagement events'],
          data_points: [
            `${(summary?.total_training_events ?? 0).toLocaleString()} events recorded`,
            TRAINING_VISIBILITY_NOTE,
          ],
          confidence: 'high',
          last_updated_at: last,
        },
        'training_summary:total_events',
      );
    case 'engaged_techs':
      return finalize(
        {
          summary: 'Distinct technicians with at least one recorded training event in scope.',
          signals: ['Distinct technician engagement'],
          data_points: [
            `${(summary?.unique_technicians_engaged ?? 0).toLocaleString()} technicians engaged`,
            TRAINING_VISIBILITY_NOTE,
          ],
          confidence: 'high',
          last_updated_at: last,
        },
        'training_summary:engaged_techs',
      );
    case 'avg_per_tech':
      return finalize(
        {
          summary: 'Average distinct artifacts engaged with per engaged technician.',
          signals: ['Distinct artifacts per technician'],
          data_points: [
            summary?.avg_artifacts_per_technician != null
              ? `Current value: ${Number(summary.avg_artifacts_per_technician).toFixed(2)}`
              : 'No engaged technicians yet',
            TRAINING_VISIBILITY_NOTE,
          ],
          confidence: 'medium',
          last_updated_at: last,
        },
        'training_summary:avg_per_tech',
      );
    case 'top_format':
      return finalize(
        {
          summary: 'Most-used artifact format by total event count in scope.',
          signals: ['Format event totals'],
          data_points: [
            summary?.top_artifact_type
              ? `Top format: ${summary.top_artifact_type}`
              : 'No format dominates yet',
            TRAINING_VISIBILITY_NOTE,
          ],
          confidence: 'medium',
          last_updated_at: last,
        },
        'training_summary:top_format',
      );
  }
}

export function explainTrainingUsage(row: ScopeTrainingArtifactRow): OptiBayExplanation {
  return finalize(
    {
      summary: 'Usage counts how many times this artifact has been engaged with in your scope.',
      signals: ['Engagement events', 'Distinct technician count'],
      data_points: [
        `${row.total_uses.toLocaleString()} total uses`,
        `${row.unique_technicians.toLocaleString()} distinct technicians`,
        TRAINING_VISIBILITY_NOTE,
      ],
      confidence: row.total_uses >= 10 ? 'high' : row.total_uses >= 3 ? 'medium' : 'low',
      sample_size: row.total_uses,
      last_updated_at: row.last_used_at ?? undefined,
    },
    'training_usage',
  );
}

export function explainTechnicianEngagement(
  row: ScopeTechnicianTrainingRow,
): OptiBayExplanation {
  return finalize(
    {
      summary: 'Engagement counts this technician\u2019s recorded training events and distinct artifacts.',
      signals: ['Training events count', 'Unique artifacts engaged'],
      data_points: [
        `${row.training_events_count.toLocaleString()} events`,
        `${row.unique_artifacts_used.toLocaleString()} distinct artifacts`,
        TRAINING_VISIBILITY_NOTE,
      ],
      confidence:
        row.training_events_count >= 10
          ? 'high'
          : row.training_events_count >= 3
            ? 'medium'
            : 'low',
      sample_size: row.training_events_count,
      last_updated_at: row.last_training_at ?? undefined,
    },
    'training_engagement',
  );
}

// ─────────────────────────── R76 — Observed Impact ───────────────────────────

export function explainObservedImpact(row: ScopeArtifactIntelligenceRow): OptiBayExplanation {
  const signals: string[] = [];
  if (row.efficiency_delta !== null) signals.push('Efficiency before/after delta');
  if (row.comeback_delta !== null) signals.push('Comeback rate before/after delta');
  if (row.diagnostic_delta !== null) signals.push('Diagnostic accuracy before/after delta');
  if (row.flags?.length) signals.push(...row.flags.map((f) => `Flag: ${f}`));

  return finalize(
    {
      summary:
        'Observed change compares the windows of completed jobs each technician finished before and after engaging with this artifact. Correlation only \u2014 not causation.',
      signals,
      data_points: [
        `Window size: ${BEFORE_AFTER_WINDOW_N} jobs each side per technician`,
        `Sample: ${row.sample_size} qualifying technician${row.sample_size === 1 ? '' : 's'}`,
        `No-change band: |delta| < ${NO_CHANGE_DELTA}`,
        `Diagnostic delta requires \u2265 ${DIAGNOSTIC_DELTA_MIN_PER_SIDE} outcomes per side`,
      ],
      confidence: row.confidence_level,
      sample_size: row.sample_size,
      last_updated_at: row.last_used_at ?? undefined,
    },
    'observed_impact',
  );
}

// ─────────────────────────── R78 — Fit suggestions ───────────────────────────

const SIGNAL_LABELS: Record<FitSignalKey, string> = {
  high_efficiency: `High efficiency (\u2265 ${FIT_SIGNAL_THRESHOLDS.high_efficiency.min_eff} across \u2265 ${FIT_SIGNAL_THRESHOLDS.high_efficiency.min_jobs} jobs)`,
  low_comeback_rate: `Low comeback rate (\u2264 ${FIT_SIGNAL_THRESHOLDS.low_comeback_rate.max_rate * 100}% across \u2265 ${FIT_SIGNAL_THRESHOLDS.low_comeback_rate.min_jobs} jobs)`,
  strong_diagnostic_accuracy: `Strong diagnostic accuracy (\u2265 ${FIT_SIGNAL_THRESHOLDS.strong_diagnostic_accuracy.min_acc * 100}% over \u2265 ${FIT_SIGNAL_THRESHOLDS.strong_diagnostic_accuracy.min_outcomes} outcomes)`,
  high_job_volume: `High job volume (\u2265 ${FIT_SIGNAL_THRESHOLDS.high_job_volume.min_jobs} completed jobs)`,
  recent_training_activity: `Recent training activity (\u2265 ${FIT_SIGNAL_THRESHOLDS.recent_training_activity.min_events} event in last ${FIT_SIGNAL_THRESHOLDS.recent_training_activity.window_days} days)`,
};

export function explainFitSuggestion(s: ScopeFitSuggestion): OptiBayExplanation {
  const trueSignals = (Object.entries(s.fit_signals) as [FitSignalKey, boolean][])
    .filter(([, v]) => v)
    .map(([k]) => SIGNAL_LABELS[k]);

  return finalize(
    {
      summary: s.reason_summary,
      signals: trueSignals,
      data_points: [
        `Based on ${s.jobs_completed.toLocaleString()} completed jobs`,
        s.diagnostic_sample_size > 0
          ? `Across ${s.diagnostic_sample_size} diagnostic outcome${s.diagnostic_sample_size === 1 ? '' : 's'}`
          : 'No diagnostic outcomes recorded',
        `Confidence rule: ${FIT_CONFIDENCE_RULES[s.confidence_level]}`,
      ],
      confidence: s.confidence_level,
      sample_size: s.jobs_completed,
      last_updated_at: s.last_computed_at,
    },
    'fit_suggestion',
  );
}
